import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { z } from 'zod'

declare global {
  interface Window {
    grecaptcha?: {
      reset?: () => void
      execute: (siteKey: string, options: { action: string }) => Promise<string>
      ready: (callback: () => void) => void
    }
  }
}

const RECAPTCHA_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_KEY
const RECAPTCHA_HEADER = 'x-grecaptcha'
const RECAPTCHA_CONFIG = {
  MAX_RETRIES: 2,
  EXECUTE_TIMEOUT: 5000,
  // Bounds the wait for a script request that neither loads nor errors.
  READY_TIMEOUT: 15000,
  SCRIPT_ID: 'recaptcha-script',
  BADGE_SELECTOR: '.grecaptcha-badge',
  INITIALIZATION_TIMEOUT: 5000,
  SCRIPT_RETRY_MAX: 2,
  SCRIPT_RETRY_DELAYS: [1000, 2000],
} as const

const reCaptchaSchema = z.object({
  reset: z.function().optional(),
  ready: z.function({ input: [z.function()] }),
  execute: z.function({
    input: [z.string(), z.object({ action: z.string() })],
    output: z.promise(z.string()),
  }),
})

// Each action except ISSUE_MFA_CHALLENGE matches the RECAPTCHA_ACTION_NAME of the settle-server mutation it
// protects; the server does not check ISSUE_MFA_CHALLENGE tokens yet.
export type RecaptchaActions =
  | 'LOGIN'
  | 'ISSUE_MFA_CHALLENGE'
  | 'SIGN_UP'
  | 'SIGN_UP_GOOGLE'
  | 'ACCEPT_INVITATION'
  | 'ACCEPT_INVITATION_GOOGLE'
  | 'ACCEPT_VENDOR_INVITATION'
  | 'ACCEPT_VENDOR_INVITATION_GOOGLE'
  | 'NEW_PASSWORD_REQUEST'
  | 'CREATE_PAYER_INVITATION'

export interface ReCaptchaVerifier<Action extends RecaptchaActions = RecaptchaActions> {
  verify(action: Action): Promise<Record<string, string>>
}

const getRetryDelay = (attempt: number) => {
  const delays = RECAPTCHA_CONFIG.SCRIPT_RETRY_DELAYS

  return delays[Math.min(attempt, delays.length) - 1] ?? delays[0]
}

interface ExecuteRecaptchaParams {
  reCaptchaKey: string
  action: RecaptchaActions
}

export const useReCaptcha = () => {
  const [scriptLoaded, setScriptLoaded] = useState(false)
  const [clientReady, setClientReady] = useState(false)
  const [loadingError, setLoadingError] = useState<string | null>(null)
  const settledRef = useRef(false)
  const settleWaitersRef = useRef<(() => void)[]>([])
  const ready = scriptLoaded && clientReady && !loadingError

  useEffect(() => {
    if (ready || loadingError) {
      settledRef.current = true
      settleWaitersRef.current.splice(0).forEach((resolve) => resolve())
    }
  }, [ready, loadingError])

  useEffect(() => {
    if (!RECAPTCHA_KEY) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- legacy, see STL-5836
      setLoadingError('reCAPTCHA key is not configured')
      return
    }

    let disposed = false
    const timers = new Set<ReturnType<typeof setTimeout>>()
    const schedule = (callback: () => void, delay: number) => {
      const timer = setTimeout(() => {
        timers.delete(timer)
        callback()
      }, delay)

      timers.add(timer)

      return timer
    }

    const loadScript = (attempt: number) => {
      if (disposed) {
        return
      }

      setScriptLoaded(false)
      setClientReady(false)
      setLoadingError(null)

      // Remove existing script if any
      const existingScript = document.getElementById(RECAPTCHA_CONFIG.SCRIPT_ID)

      if (existingScript) {
        existingScript.remove()
      }

      const script = document.createElement('script')

      script.id = RECAPTCHA_CONFIG.SCRIPT_ID
      script.src = `https://www.recaptcha.net/recaptcha/api.js?render=${RECAPTCHA_KEY}`
      script.async = true
      script.defer = true

      script.onload = () => {
        let initializationTimeout: ReturnType<typeof setTimeout>

        setScriptLoaded(true)
        setLoadingError(null)

        // Check if client is ready after script loads
        if (window.grecaptcha && 'ready' in window.grecaptcha) {
          let isClientReady = false

          window.grecaptcha.ready(() => {
            isClientReady = true
            setClientReady(true)
            clearTimeout(initializationTimeout)
          })

          // Add timeout for client initialization
          initializationTimeout = schedule(() => {
            if (!isClientReady) {
              if (attempt < RECAPTCHA_CONFIG.SCRIPT_RETRY_MAX) {
                schedule(() => loadScript(attempt + 1), getRetryDelay(attempt))
              } else {
                setLoadingError('reCAPTCHA initialization failed after retries')
              }
            }
          }, RECAPTCHA_CONFIG.INITIALIZATION_TIMEOUT)
        } else {
          setLoadingError('reCAPTCHA script loaded but grecaptcha not available')
        }
      }

      script.onerror = () => {
        if (attempt < RECAPTCHA_CONFIG.SCRIPT_RETRY_MAX) {
          schedule(() => loadScript(attempt + 1), getRetryDelay(attempt))
        } else {
          setScriptLoaded(false)
          setClientReady(false)
          setLoadingError('Failed to load reCAPTCHA script after retries')
        }
      }

      document.body.appendChild(script)
    }

    loadScript(1)

    return () => {
      disposed = true
      timers.forEach((timer) => clearTimeout(timer))
      setScriptLoaded(false)
      setClientReady(false)
      setLoadingError(null)

      try {
        const scriptElement = document.getElementById(RECAPTCHA_CONFIG.SCRIPT_ID)
        const badge = document.querySelector(RECAPTCHA_CONFIG.BADGE_SELECTOR)

        if (scriptElement) {
          scriptElement.remove()
        }

        if (badge?.parentElement) {
          badge.parentElement.remove()
        }
      } catch {
        // Ignore cleanup errors
      }
    }
  }, [])

  const getReCaptcha = useCallback(() => {
    if (typeof window === 'undefined') {
      throw new Error('reCAPTCHA is not available in server-side environment')
    }

    if (!window.grecaptcha) {
      throw new Error('reCAPTCHA script not loaded')
    }

    return reCaptchaSchema.parse(window.grecaptcha)
  }, [])

  const executeRecaptcha = useCallback(
    ({ action, reCaptchaKey }: ExecuteRecaptchaParams): Promise<string> => {
      const recaptcha = getReCaptcha()

      return new Promise<string>((resolve, reject) => {
        // Armed before `ready` so a client that never becomes ready still rejects.
        const timeout = setTimeout(() => {
          reject(new Error('recaptcha.execute stuck'))
        }, RECAPTCHA_CONFIG.EXECUTE_TIMEOUT)

        recaptcha.ready(() => {
          recaptcha
            .execute(reCaptchaKey, { action })
            .then(resolve)
            .catch(reject)
            .finally(() => {
              clearTimeout(timeout)
            })
        })
      })
    },
    [getReCaptcha],
  )

  const getRecaptchaAPIToken = useCallback(
    (action: RecaptchaActions): Promise<string> => {
      if (!RECAPTCHA_KEY) {
        throw new Error('reCAPTCHA key is not configured')
      }

      return executeRecaptcha({ action, reCaptchaKey: RECAPTCHA_KEY })
    },
    [executeRecaptcha],
  )

  /** Resolves to no header when no token can be minted. */
  const verify = useCallback(
    async (action: RecaptchaActions): Promise<Record<string, string>> => {
      // Only a wait, not a gate: a client that becomes ready after the loader gave up can still mint a token.
      if (!settledRef.current) {
        await new Promise<void>((resolve) => {
          const readyTimeout = setTimeout(resolve, RECAPTCHA_CONFIG.READY_TIMEOUT)

          settleWaitersRef.current.push(() => {
            clearTimeout(readyTimeout)
            resolve()
          })
        })
      }

      for (let attempt = 1; attempt <= RECAPTCHA_CONFIG.MAX_RETRIES; attempt++) {
        try {
          const token = await getRecaptchaAPIToken(action)

          return { [RECAPTCHA_HEADER]: token }
        } catch {
          // Falls through to the next attempt.
        }
      }

      return {}
    },
    [getRecaptchaAPIToken],
  )

  return useMemo(
    () => ({
      verify,
      ready,
    }),
    [verify, ready],
  )
}
